<template>
  <div
    class="doc-platform-info"
    :class="{ 'doc-platform-info--bordered': bordered }"
  >
    <div
      v-if="collapsible"
      class="platform-toggle"
      role="button"
      tabindex="0"
      @click="toggleExpanded"
      @keydown.enter.prevent="toggleExpanded"
      @keydown.space.prevent="toggleExpanded"
    >
      <AntButton
        type="link"
        size="small"
        :title="expanded ? '收合' : '展開'"
        class="platform-toggle-btn"
        tabindex="-1"
        @click.stop="toggleExpanded"
      >
        <CaretRightOutlined v-if="!expanded" />
        <CaretDownOutlined v-else />
      </AntButton>
      <span class="platform-toggle-label">{{ title }}</span>
    </div>

    <div
      v-show="!collapsible || expanded"
      class="platform-info"
      :class="{ 'platform-info--indented': collapsible }"
    >
      <div class="platform-info-row">
        <div class="platform-info-label">子本 Platforms:</div>
        <div v-if="docData?.platforms?.length">
          <span v-for="item in docData.platforms" :key="item">
            <AntTag v-if="shouldShowPlatformTag(item)">
              {{ platformsMap[item] }}
            </AntTag>
          </span>
        </div>
        <AntTag v-else>ALL</AntTag>
      </div>

      <div
        v-if="showDocExcludedPlatforms"
        class="platform-info-row"
      >
        <div class="platform-info-label">子本 Excluded Platforms:</div>
        <div v-if="docExcludedPlatforms.length">
          <span v-for="item in docExcludedPlatforms" :key="item">
            <AntTag v-if="shouldShowPlatformTag(item)">
              {{ platformsMap[item] }}
            </AntTag>
          </span>
        </div>
        <span v-else class="platform-info-empty">無</span>
      </div>

      <AntDivider v-if="showSectionPlatforms" class="platform-info-divider" />

      <div v-if="showSectionPlatforms" class="platform-info-row">
        <div class="platform-info-label">區塊 Platforms:</div>
        <div v-if="sectionPlatforms?.length">
          <span v-for="item in sectionPlatforms" :key="item">
            <AntTag v-if="shouldShowPlatformTag(item)">
              {{ platformsMap[item] }}
            </AntTag>
          </span>
        </div>
        <AntTag v-else>ALL</AntTag>
      </div>

      <div
        v-if="showSectionExcludedPlatforms"
        class="platform-info-row"
      >
        <div class="platform-info-label">區塊 Excluded Platforms:</div>
        <div v-if="sectionExcludedPlatforms?.length">
          <span v-for="item in sectionExcludedPlatforms" :key="item">
            <AntTag v-if="shouldShowPlatformTag(item)">
              {{ platformsMap[item] }}
            </AntTag>
          </span>
        </div>
        <span v-else class="platform-info-empty">無</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, computed } from 'vue'

import {
  Tag as AntTag,
  Button as AntButton,
  Divider as AntDivider,
} from 'ant-design-vue'

import CaretRightOutlined from '@ant-design/icons-vue/lib/icons/CaretRightOutlined'
import CaretDownOutlined from '@ant-design/icons-vue/lib/icons/CaretDownOutlined'

export default {
  name: 'DocPlatformInfo',
  components: {
    AntTag,
    AntButton,
    AntDivider,
    CaretRightOutlined,
    CaretDownOutlined,
  },
  props: {
    docData: {
      type: Object,
      default: null,
    },
    sectionPlatforms: {
      type: Array,
      default: null,
    },
    sectionExcludedPlatforms: {
      type: Array,
      default: null,
    },
    showSectionPlatforms: {
      type: Boolean,
      default: false,
    },
    platformsMap: {
      type: Object,
      required: true,
    },
    hideUnknownPlatforms: {
      type: Boolean,
      default: false,
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    defaultExpanded: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: 'Platforms 設定',
    },
    bordered: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const expanded = ref(props.defaultExpanded)

    const shouldShowPlatformTag = platformId => !props.hideUnknownPlatforms || props.platformsMap[platformId]

    /** 子本未設 platforms 白名單時，以 excludedPlatforms 定義可見範圍 */
    const showDocExcludedPlatforms = computed(() => !props.docData?.platforms?.length && props.docData?.excludedPlatforms?.length)

    const docExcludedPlatforms = computed(() => props.docData?.excludedPlatforms || [])

    /** 區塊未設 platforms 白名單時，以 excludedPlatforms 定義可見範圍 */
    const showSectionExcludedPlatforms = computed(() => (
      props.showSectionPlatforms && !props.sectionPlatforms?.length && props.sectionExcludedPlatforms?.length
    ))

    const toggleExpanded = () => {
      expanded.value = !expanded.value
    }

    watch(
      () => props.defaultExpanded,
      (value) => {
        expanded.value = value
      },
    )

    return {
      expanded,
      shouldShowPlatformTag,
      showDocExcludedPlatforms,
      docExcludedPlatforms,
      showSectionExcludedPlatforms,
      toggleExpanded,
    }
  },
}
</script>

<style scoped>
.doc-platform-info--bordered {
  border-bottom: 1px solid #eee;
  padding-bottom: 16px;
  margin-bottom: 24px;
}

.platform-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
}

.platform-toggle-label {
  font-weight: 500;
  color: #1890ff;
}

.platform-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  padding: 0;
  height: auto;
  flex-shrink: 0;
}

.platform-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.platform-info--indented {
  margin-top: 8px;
  margin-left: 24px;
}

.platform-info-row {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.platform-info-label {
  flex-shrink: 0;
  line-height: 28px;
}

.platform-info-empty {
  color: rgba(0, 0, 0, 0.45);
  line-height: 28px;
}

.platform-info-divider {
  margin: 8px 0;
}

:deep(.ant-tag) {
  margin: 2px 8px 2px 0;
}
</style>
